import { Directive, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { Player, YoutubeService } from '@common/services/youtube.service';
import { waitUntil$ } from '@common/util/dom';
import { EMPTY, merge, Observable, Subject } from 'rxjs';
import { timeoutWith, takeUntil } from 'rxjs/operators';

/**
 * A directive that is used in stead of the Youtube Pipe.
 * This makes it more compatible with the YT iframe API and Google Analytics.
 * To opt-in:
 * - add the `containsyoutube` directive to the element that contains the markdown
 * - from CMS, you need to change the signature from `{{youtube-id}}` to `<div class="youtube-id"></div>`.
 *   (class is necessary because other attributes get sanitized by Angular)
 */
@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: '[containsyoutube]',
    standalone: false
})
export class ContainsYoutubeDirective implements OnInit, OnDestroy {
    private element: HTMLElement;

    pattern = /youtube-([a-zA-Z0-9\-_]+)/;

    constructor(el: ElementRef, private youtube: YoutubeService) {
        this.element = el?.nativeElement;
    }

    async ngOnInit() {
        await this.waitForInnerHtml();
        if (!this.pattern.test(this.element.innerHTML)) return;
        this.setupPlayers().pipe(takeUntil(this._destroyed$)).subscribe();
    }

    private async waitForInnerHtml() {
        return await waitUntil$(() => !!this.element.innerHTML)
            .pipe(timeoutWith(2000, EMPTY))
            .toPromise();
    }

    private setupPlayers(): Observable<Player> {
        const elements = this.getAllVideoElements();
        const players = elements.map((el) => this.setupPlayer(el));
        return merge(...players);
    }

    private getAllVideoElements(): HTMLElement[] {
        const selector = '[class*="youtube"]';
        const elements = this.element.querySelectorAll<HTMLElement>(selector);
        return Array.from(elements);
    }

    private setupPlayer(element: HTMLElement) {
        const videoId = element.className.match(this.pattern)?.[1];
        return this.youtube.setupPlayerWithThumbnail(element, videoId);
    }

    private _destroyed$ = new Subject<void>();

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }
}
